import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useSearchParams } from '@/hooks/useSearchParams'
import { useRef, useState } from 'react'

/**
 * Custom hook to manage search changes and URL query parameter updates between
 * the search overlay and a collection page.
 *
 * When a search query is entered, applied search filters and the query string are updated in the URL
 * to reflect the search state.
 * If the search query is cleared, the previous URL and relevant collection page filters are
 * restored if they were applied.
 *
 * @returns An object containing:
 * - `hasChanged`: A `RefObject<boolean>` indicating whether the search query has changed.
 * - `handleSearchChange`: A function to handle updates to the search query.
 *
 * @example
 * ```tsx
 * const { hasChanged, handleSearchChange } = useSearchHistory();
 *
 * const onSearch = (query: string) => {
 *   handleSearchChange(query);
 * };
 * ```
 */
export const useSearchHistory = () => {
  const { clearSearchParams, replace, setParamsFromUrl } = useSearchParams()
  const [historyUrl, setHistoryUrl] = useState<string | undefined>()
  const { getFlag } = useFeatureFlags()
  const isSearchFiltersEnabled = getFlag('enable_search_filters')

  const hasChanged = useRef(false)

  const handleSearchChange = (search: string) => {
    // TODO: Remove this block once the feature flag is rolled out
    // This is a temporary block for backwards compatibility
    if (!isSearchFiltersEnabled) {
      if (search) {
        hasChanged.current = true
        replace({ q: search })
      } else {
        replace({ q: undefined })
      }
      return
    }

    if (search) {
      hasChanged.current = true
      if (!window.location.pathname.includes('/search')) {
        setHistoryUrl(
          `${window.location.pathname || ''}${window.location.hash || ''}${window.location.search || ''}`,
        )

        clearSearchParams()
        window.history.replaceState(
          {},
          '',
          `/search?q=${encodeURIComponent(search)}`,
        )
        setParamsFromUrl()
      } else {
        replace({ q: search })
      }
    } else {
      replace({ q: undefined })

      if (window.location.pathname === '/search') {
        if (historyUrl) {
          // I do not understand why we need to do this first, but it works
          clearSearchParams()
          window.history.replaceState({}, '', historyUrl)
          setParamsFromUrl()
        }
      }
    }
  }

  return { hasChanged, handleSearchChange }
}
